/* One call's report — how far it reached, and who came.
 *
 * TWO HALVES, AND THEY ANSWER DIFFERENT QUESTIONS. Reach is about the
 * invitation: who it was for, who was told, who opened it. Turnout is about
 * the room. Keeping them apart on the screen matters, because a call that
 * reached everybody and drew nobody is a different problem from one that
 * never got announced.
 *
 * Turnout stays hidden until a call has actually opened. A row of zeroes on
 * a call that has not run yet reads as failure, and there is no honest
 * percentage to put next to it.
 */
import { useEffect, useState } from 'react'
import {
  ArrowLeft,
  ChevronRight,
  Download,
  Hand,
  Loader2,
  Mic,
  Search,
  UserCheck,
  UserX,
  Users,
} from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Drawer } from '@/components/ui/drawer'
import { Card } from '@/components/ui/card'
import { useCallAnalytics, useCallAttendance, useMe } from '@/api/queries'
import { cn } from '@/lib/utils'
import { useDebounced } from '@/lib/useDebounced'
import type { CallAnalytics } from '@/types/api'

const KIND_NAME: Record<string, string> = {
  audio: 'Meeting',
  video: 'Video meeting',
  live: 'Live',
}

export function CallReportPage() {
  const { callId } = useParams<{ callId: string }>()
  const { data: me } = useMe()
  const churchId = me?.church_id ?? null
  const report = useCallAnalytics(churchId, callId ?? null)

  if (report.isLoading) {
    return <Card className="p-s6 text-center text-ink-2">Loading…</Card>
  }

  if (report.isError || !report.data) {
    return (
      <Card className="p-s6 text-center text-base text-ink-2">
        That call’s report is not available.
      </Card>
    )
  }

  const r = report.data

  return (
    <div className="space-y-s4">
      <div>
        <Link
          to="/calls"
          className="inline-flex items-center gap-1.5 text-sm text-ink-2 hover:text-ink"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          Calls
        </Link>
        <h1 className="mt-s2 text-xl font-extrabold tracking-tight">
          {r.title}
        </h1>
        <p className="mt-0.5 text-base text-ink-2">
          {KIND_NAME[r.kind] ?? 'Call'} · {r.audience_label}
          {r.duration_seconds !== null
            ? ` · ran for ${duration(r.duration_seconds)}`
            : ''}
        </p>
      </div>

      <Reach report={r} />
      <Turnout report={r} />
      <Recording call={r} />
      <Attendance
        churchId={churchId}
        callId={callId!}
        live={r.kind === 'live'}
        ran={r.attended !== null}
      />
    </div>
  )
}

/* ------------------------------------------------------------------ reach */

function Reach({ report: r }: { report: CallAnalytics }) {
  /* Told is below invited whenever the organiser is in their own audience.
     Said plainly here, because an unexplained gap looks like a fault. */
  const organiserGap = r.invited - r.told

  return (
    <Card className="p-s4">
      <h2 className="text-md font-bold">How far it reached</h2>
      <div className="mt-s3 flex flex-wrap gap-x-s6 gap-y-s3">
        <Stat value={r.invited} label="invited" />
        <Stat value={r.told} label="told on their phone" />
        <Stat
          value={r.opened}
          label="opened it"
          suffix={r.open_rate !== null ? `${r.open_rate}%` : undefined}
        />
      </div>

      {organiserGap > 0 ? (
        <p className="mt-s3 border-t border-line-2 pt-s3 text-sm text-ink-2">
          Whoever arranged the call is counted as invited but not told — we do
          not send somebody a notification about their own call.
        </p>
      ) : null}
    </Card>
  )
}

/* ---------------------------------------------------------------- turnout */

function Turnout({ report: r }: { report: CallAnalytics }) {
  /* A live is watched, not joined: the same counts, in its own words. */
  const live = r.kind === 'live'
  /* Null means the room never opened, which is not the same as nobody
     coming. Nothing here can be said honestly yet, so nothing is. */
  if (r.attended === null) {
    return (
      <Card className="p-s4">
        <h2 className="text-md font-bold">{live ? 'Who watched' : 'Who came'}</h2>
        <p className="mt-s2 text-base text-ink-2">
          {live ? 'This live has not run yet.' : 'This call has not run yet.'}{' '}
          Attendance appears here once it has.
        </p>
      </Card>
    )
  }

  return (
    <Card className="p-s4">
      <h2 className="text-md font-bold">{live ? 'Who watched' : 'Who came'}</h2>
      <div className="mt-s3 flex flex-wrap gap-x-s6 gap-y-s3">
        <Stat
          value={r.attended}
          label={live ? 'watched' : 'came'}
          suffix={r.turnout_rate !== null ? `${r.turnout_rate}%` : undefined}
          icon={Users}
        />
        {r.raised_hand !== null ? (
          <Stat value={r.raised_hand} label="put a hand up" icon={Hand} />
        ) : null}
        {r.spoke !== null ? (
          <Stat value={r.spoke} label="spoke" icon={Mic} />
        ) : null}
        {r.median_seconds !== null ? (
          <Stat
            value={duration(r.median_seconds)}
            label={live ? 'typical time watching' : 'typical stay'}
          />
        ) : null}
      </div>
    </Card>
  )
}

/* ------------------------------------------------------------------ parts */

function Stat({
  value,
  label,
  suffix,
  icon: Icon,
}: {
  value: number | string
  label: string
  suffix?: string
  icon?: React.ComponentType<{ className?: string }>
}) {
  return (
    <div>
      <div className="flex items-baseline gap-1.5">
        {Icon ? (
          <Icon className="h-3.5 w-3.5 self-center text-ink-2" aria-hidden />
        ) : null}
        <span className="text-lg font-extrabold tracking-tight">{value}</span>
        {suffix ? (
          <span className={cn('text-sm font-semibold text-ink-2')}>
            {suffix}
          </span>
        ) : null}
      </div>
      <div className="text-sm text-ink-2">{label}</div>
    </div>
  )
}

/** "34 minutes", "1h 05m" — never a bare number of seconds. */
function duration(seconds: number): string {
  if (seconds < 60) return 'under a minute'
  const mins = Math.round(seconds / 60)
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'}`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return `${h}h ${String(m).padStart(2, '0')}m`
}


/* -------------------------------------------------------------- recording */
/*
 * THE TAPE, ONCE THERE IS ONE. A recording exists only after the call has
 * ended — the file is written by the recorder stopping — so a live call
 * shows nothing here rather than an empty player.
 */

function Recording({ call }: { call: CallAnalytics }) {
  const url = call.recording_url
  if (!call.recording_key) return null

  return (
    <Card className="p-s4">
      <h2 className="text-md font-bold">The recording</h2>

      {url ? (
        <>
          <audio controls preload="metadata" src={url} className="mt-s3 w-full">
            <track kind="captions" />
          </audio>
          <a
            href={url}
            download
            className="mt-s2 inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline"
          >
            <Download className="h-3.5 w-3.5" aria-hidden />
            Download
          </a>
        </>
      ) : (
        /* The tape exists — the key proves it — but we have no address
           for it. Saying so beats a player that does nothing. */
        <p className="mt-s2 text-base text-ink-2">
          This call was recorded and the recording has been kept.
        </p>
      )}
    </Card>
  )
}

/* ------------------------------------------------------------- attendance */
/*
 * THE NAMES BEHIND THE NUMBERS. A turnout figure says how many came; the
 * person following up needs to know who — who spoke, who asked and was
 * never reached, who was invited and never arrived. Three different
 * conversations afterwards, and none of them can be had from a count.
 *
 * COUNTS HERE, NAMES IN A DRAWER. A congregation is hundreds of people; a
 * paragraph of names cannot be searched, sorted or followed up from. Each
 * count opens the same table, on its own side, a page at a time.
 */

type Side = 'came' | 'missed'

const PAGE_SIZE = 25

function Attendance({
  churchId,
  callId,
  live = false,
  ran,
}: {
  churchId: string | null
  callId: string
  live?: boolean
  /** False until the room has opened: nobody has "not come" to something
   *  that has not happened yet. */
  ran: boolean
}) {
  const [side, setSide] = useState<Side | null>(null)
  // One row is enough: what this asks for is the two totals.
  const summary = useCallAttendance(churchId, callId, { pageSize: 1 })
  const s = summary.data
  if (!s || s.came_total + s.missed_total === 0) return null

  const words = sideWords(live, ran)

  return (
    <Card className="p-s4">
      <h2 className="text-md font-bold">Everybody invited</h2>
      <div className="mt-s3 grid gap-s3 sm:grid-cols-2">
        <SideTile
          count={s.came_total}
          label={words.came}
          icon={UserCheck}
          tone="success"
          onClick={() => setSide('came')}
        />
        <SideTile
          count={s.missed_total}
          label={words.missed}
          icon={UserX}
          tone="muted"
          onClick={() => setSide('missed')}
        />
      </div>

      <GuestList
        churchId={churchId}
        callId={callId}
        live={live}
        ran={ran}
        side={side}
        onSide={setSide}
      />
    </Card>
  )
}

function sideWords(live: boolean, ran = true): Record<Side, string> {
  if (!ran) {
    return live
      ? { came: 'Watching', missed: 'Not watching yet' }
      : { came: 'Joined', missed: 'Not joined yet' }
  }
  return live
    ? { came: 'Watched', missed: 'Did not watch' }
    : { came: 'Came', missed: 'Did not come' }
}

function SideTile({
  count,
  label,
  icon: Icon,
  tone,
  onClick,
}: {
  count: number
  label: string
  icon: React.ComponentType<{ className?: string }>
  tone: 'success' | 'muted'
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={count === 0}
      className={cn(
        'flex items-center gap-s3 rounded border border-line-2 bg-surface px-s4 py-s3 text-left',
        'transition-colors hover:bg-hover focus-visible:shadow-focus focus-visible:outline-none',
        'disabled:cursor-default disabled:hover:bg-surface',
      )}
    >
      <span
        className={cn(
          'grid h-9 w-9 shrink-0 place-items-center rounded-pill',
          tone === 'success'
            ? 'bg-success-soft text-success-ink'
            : 'bg-surface-2 text-ink-2',
        )}
      >
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-lg font-extrabold tracking-tight">
          {count}
        </span>
        <span className="block text-sm text-ink-2">{label}</span>
      </span>
      {count > 0 ? (
        <span className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-brand">
          See who
          <ChevronRight className="h-4 w-4" aria-hidden />
        </span>
      ) : null}
    </button>
  )
}

function GuestList({
  churchId,
  callId,
  live,
  ran,
  side,
  onSide,
}: {
  churchId: string | null
  callId: string
  live: boolean
  ran: boolean
  side: Side | null
  onSide: (side: Side | null) => void
}) {
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const q = useDebounced(query.trim())
  const words = sideWords(live, ran)

  // A new side or a new search starts from the top; staying on page 4 of a
  // list that now has one page shows an empty table.
  useEffect(() => setPage(1), [side, q])
  // Closing forgets the search, so reopening shows the whole list.
  useEffect(() => {
    if (side === null) setQuery('')
  }, [side])

  const list = useCallAttendance(
    churchId,
    callId,
    { came: side === 'came', q, page, pageSize: PAGE_SIZE },
    side !== null,
  )
  const data = list.data
  const total = data?.total ?? 0
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const rows = data?.items ?? []
  const came = side === 'came'

  return (
    <Drawer
      open={side !== null}
      onOpenChange={(open) => (open ? null : onSide(null))}
      title={ran ? (live ? 'Who watched' : 'Who came') : "Who's invited"}
      description={
        ran
          ? 'Everybody this was for, and what they did.'
          : 'Everybody this is for. Who came shows here once it has run.'
      }
      footer={
        total > 0 ? (
          <div className="flex items-center justify-between gap-s3">
            <span className="text-sm text-ink-2">
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)}{' '}
              of {total}
            </span>
            <div className="flex items-center gap-s2">
              <Button
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                Previous
              </Button>
              <span className="text-sm text-ink-2">
                Page {page} of {lastPage}
              </span>
              <Button
                size="sm"
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                disabled={page >= lastPage}
              >
                Next
              </Button>
            </div>
          </div>
        ) : null
      }
    >
      <div className="sticky top-0 z-10 space-y-s3 border-b border-line-2 bg-surface px-s4 py-s3">
        <div role="tablist" className="flex gap-s2">
          {(['came', 'missed'] as const).map((k) => {
            const count =
              k === 'came' ? data?.came_total : data?.missed_total
            return (
              <button
                key={k}
                role="tab"
                aria-selected={side === k}
                onClick={() => onSide(k)}
                className={cn(
                  'rounded-pill border px-s3 py-1.5 text-sm font-semibold transition-colors',
                  side === k
                    ? 'border-brand bg-brand-soft text-brand'
                    : 'border-line-2 text-ink-2 hover:bg-hover',
                )}
              >
                {words[k]}
                {count !== undefined ? ` · ${count}` : ''}
              </button>
            )
          })}
        </div>
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-s3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-2"
            aria-hidden
          />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name"
            aria-label="Search the guest list"
            className="w-full rounded-pill border border-line-2 bg-surface-2 py-s2 pl-[38px] pr-s3 text-base outline-none focus:shadow-focus"
          />
        </div>
      </div>

      {list.isLoading ? (
        <div className="flex justify-center py-s6">
          <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
        </div>
      ) : rows.length === 0 ? (
        <p className="px-s4 py-s6 text-center text-base text-ink-2">
          {q ? `Nobody here matches “${q}”.` : 'Nobody here.'}
        </p>
      ) : (
        <table className={cn('w-full text-left', list.isFetching && 'opacity-70')}>
          <thead>
            <tr className="border-b border-line-2 text-xs font-semibold uppercase tracking-wide text-ink-2">
              <th className="px-s4 py-s2">Name</th>
              {came ? (
                <>
                  <th className="px-s2 py-s2">{live ? 'Started watching' : 'Joined'}</th>
                  <th className="px-s2 py-s2">{live ? 'Watched' : 'Stayed'}</th>
                  {live ? null : <th className="px-s4 py-s2">Took part</th>}
                </>
              ) : (
                <th className="px-s4 py-s2">Church role</th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.user_id} className="border-b border-line-2 last:border-0">
                <td className="px-s4 py-s2">
                  <div className="flex items-center gap-s3">
                    <Avatar name={p.name} src={p.avatar_url} size="sm" />
                    <div className="min-w-0">
                      <div className="truncate text-base font-semibold">{p.name}</div>
                      {came && p.role_name ? (
                        <div className="truncate text-sm text-ink-2">{p.role_name}</div>
                      ) : null}
                    </div>
                  </div>
                </td>
                {came ? (
                  <>
                    <td className="whitespace-nowrap px-s2 py-s2 text-sm text-ink-2">
                      {p.joined_at ? clock(p.joined_at) : '—'}
                    </td>
                    <td className="whitespace-nowrap px-s2 py-s2 text-sm text-ink-2">
                      {p.minutes !== null ? `${p.minutes} min` : '—'}
                    </td>
                    {live ? null : (
                      <td className="px-s4 py-s2">
                        <div className="flex flex-wrap gap-1">
                          {p.spoke ? (
                            <span className="rounded-pill bg-success-soft px-s2 py-0.5 text-xs font-semibold text-success-ink">
                              spoke
                            </span>
                          ) : null}
                          {p.hands_raised ? (
                            <span className="rounded-pill bg-surface-2 px-s2 py-0.5 text-xs font-semibold text-ink-2">
                              asked to speak
                              {p.hands_raised > 1 ? ` ${p.hands_raised}×` : ''}
                            </span>
                          ) : null}
                          {!p.spoke && !p.hands_raised ? (
                            <span className="text-sm text-ink-3">listened</span>
                          ) : null}
                        </div>
                      </td>
                    )}
                  </>
                ) : (
                  <td className="px-s4 py-s2 text-sm text-ink-2">
                    {p.role_name ?? '—'}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Drawer>
  )
}

/** "6:42 pm" — the time of day someone arrived. */
function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  })
}
